import { store } from '../state.js';
import { formatMoney, daysAgo } from '../utils.js';
import { DIRECTIONS_BY_ID, APPLIANCE_DIRECTIONS } from '../directionsData.js';
import { CITIES } from '../data.js';
import { showToast } from '../toast.js';

let period = '30';

function leadsInPeriod() {
    const cutoff = daysAgo(Number(period));
    return store.get().leads.filter(l => l.createdAt >= cutoff);
}

function buildFunnel(leads) {
    const total = leads.length;
    const targeted = leads.filter(l => !(l.dispute && l.dispute.result === 'refunded')).length;
    const called = leads.filter(l => ['called', 'deal', 'declined'].includes(l.status)).length;
    const deals = leads.filter(l => l.status === 'deal').length;

    const steps = [
        { label: 'Заявки', value: total },
        { label: 'Целевые', value: targeted },
        { label: 'Дозвонились', value: called },
        { label: 'Сделки', value: deals }
    ];
    const max = Math.max(1, total);

    return steps.map(s => `
        <div class="rep-funnel__step">
            <div class="rep-funnel__bar-wrap"><div class="rep-funnel__bar" style="width:${Math.round((s.value / max) * 100)}%"></div></div>
            <div class="rep-funnel__label">${s.label}</div>
            <div class="rep-funnel__value">${s.value}</div>
        </div>
    `).join('');
}

function buildSources(leads) {
    const counts = {};
    leads.forEach(l => { counts[l.source] = (counts[l.source] || 0) + 1; });
    const total = leads.length || 1;
    return Object.entries(counts).map(([label, count]) => `
        <div class="rep-sources__row">
            <span>${label}</span>
            <div class="rep-sources__bar-wrap"><div class="rep-sources__bar" style="width:${Math.round((count / total) * 100)}%"></div></div>
            <span>${count}</span>
        </div>
    `).join('') || '<div class="rep-empty">Нет данных</div>';
}

function buildDirectionsTable(leads) {
    const rows = APPLIANCE_DIRECTIONS.map(d => {
        const dirLeads = leads.filter(l => l.directionId === d.id);
        const deals = dirLeads.filter(l => l.status === 'deal');
        const conv = dirLeads.length ? Math.round((deals.length / dirLeads.length) * 100) : 0;
        const avgDeal = deals.length ? deals.reduce((s, l) => s + (l.dealAmount || 0), 0) / deals.length : 0;
        return { title: d.title, count: dirLeads.length, deals: deals.length, conv, avgDeal };
    }).filter(r => r.count > 0).sort((a, b) => b.count - a.count);

    if (!rows.length) return '<div class="rep-empty">Нет данных за период</div>';

    return `
        <table class="rep-table">
            <thead><tr><th>Направление</th><th>Заявки</th><th>Сделки</th><th>Конверсия</th><th>Цена сделки</th></tr></thead>
            <tbody>
                ${rows.map(r => `<tr><td>${r.title}</td><td>${r.count}</td><td>${r.deals}</td><td>${r.conv}%</td><td>${r.avgDeal ? formatMoney(r.avgDeal) : '—'}</td></tr>`).join('')}
            </tbody>
        </table>
    `;
}

function buildCitiesTable(leads) {
    const rows = CITIES.map(c => {
        const cityLeads = leads.filter(l => l.cityId === c.id);
        const fromRegion = cityLeads.filter(l => l.isRegion);
        const deals = cityLeads.filter(l => l.status === 'deal');
        const conv = cityLeads.length ? Math.round((deals.length / cityLeads.length) * 100) : 0;
        const avgCost = cityLeads.length ? cityLeads.reduce((s, l) => s + l.cost, 0) / cityLeads.length : 0;
        const dealsSum = deals.reduce((s, l) => s + (l.dealAmount || 0), 0);
        return { name: c.name, count: cityLeads.length, fromRegion: fromRegion.length, deals: deals.length, conv, avgCost, dealsSum };
    }).filter(r => r.count > 0).sort((a, b) => b.count - a.count);

    if (!rows.length) return '<div class="rep-empty">Нет данных за период</div>';

    return `
        <table class="rep-table">
            <thead><tr><th>Город</th><th>Заявки</th><th>Из области</th><th>Сделки</th><th>Конверсия</th><th>Ср. цена</th><th>Сумма сделок</th></tr></thead>
            <tbody>
                ${rows.map(r => `<tr><td>${r.name}</td><td>${r.count}</td><td>${r.fromRegion}</td><td>${r.deals}</td><td>${r.conv}%</td><td>${formatMoney(r.avgCost)}</td><td>${formatMoney(r.dealsSum)}</td></tr>`).join('')}
            </tbody>
        </table>
    `;
}

function buildHeatmap(leads) {
    const days = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
    leads.forEach(l => {
        const date = new Date(l.createdAt);
        const day = (date.getDay() + 6) % 7;
        grid[day][date.getHours()]++;
    });
    const max = Math.max(1, ...grid.flat());

    const hourCols = Array.from({ length: 15 }, (_, i) => i + 8);

    return `
        <div class="rep-heatmap">
            <div class="rep-heatmap__row rep-heatmap__row--head">
                <div></div>
                ${hourCols.map(h => `<div>${h}</div>`).join('')}
            </div>
            ${days.map((d, dayIdx) => `
                <div class="rep-heatmap__row">
                    <div class="rep-heatmap__day">${d}</div>
                    ${hourCols.map(h => {
                        const v = grid[dayIdx][h];
                        const opacity = v ? 0.15 + (v / max) * 0.85 : 0;
                        return `<div class="rep-heatmap__cell" style="background:rgba(41,182,255,${opacity.toFixed(2)})" title="${v}"></div>`;
                    }).join('')}
                </div>
            `).join('')}
        </div>
    `;
}

export function renderReports(container) {
    const leads = leadsInPeriod();
    const spent = leads.reduce((s, l) => s + l.cost, 0);
    const refunded = leads.filter(l => l.dispute && l.dispute.result === 'refunded').reduce((s, l) => s + l.cost, 0);
    const dealsSum = leads.filter(l => l.status === 'deal').reduce((s, l) => s + (l.dealAmount || 0), 0);
    const roi = spent > 0 ? Math.round((dealsSum / spent) * 100) : 0;

    container.innerHTML = `
        <div class="rep-head">
            <div class="ov-period">
                <button type="button" class="ov-period__btn${period === '7' ? ' active' : ''}" data-period="7">7 дней</button>
                <button type="button" class="ov-period__btn${period === '30' ? ' active' : ''}" data-period="30">30 дней</button>
            </div>
            <button type="button" class="rep-send-btn" id="rep-send">Отправить отчёт на почту</button>
        </div>

        <div class="ov-stats">
            <div class="ov-stat"><div class="ov-stat__label">Потрачено</div><div class="ov-stat__val">${formatMoney(spent)}</div></div>
            <div class="ov-stat"><div class="ov-stat__label">Возвращено за брак</div><div class="ov-stat__val">${formatMoney(refunded)}</div></div>
            <div class="ov-stat"><div class="ov-stat__label">Сумма сделок</div><div class="ov-stat__val">${formatMoney(dealsSum)}</div></div>
            <div class="ov-stat ov-stat--accent"><div class="ov-stat__label">Окупаемость</div><div class="ov-stat__val">${roi}%</div></div>
        </div>

        <div class="ov-grid">
            <div class="ov-card">
                <div class="ov-card__title">Воронка</div>
                <div class="rep-funnel">${buildFunnel(leads)}</div>
            </div>
            <div class="ov-card">
                <div class="ov-card__title">Источники</div>
                <div class="rep-sources">${buildSources(leads)}</div>
            </div>
        </div>

        <div class="ov-card">
            <div class="ov-card__title">По направлениям</div>
            <div class="rep-table-wrap">${buildDirectionsTable(leads)}</div>
        </div>

        <div class="ov-card">
            <div class="ov-card__title">По городам</div>
            <div class="rep-table-wrap">${buildCitiesTable(leads)}</div>
        </div>

        <div class="ov-card">
            <div class="ov-card__title">Тепловая карта: день недели × час</div>
            <div class="rep-table-wrap">${buildHeatmap(leads)}</div>
        </div>
    `;

    container.querySelectorAll('.ov-period__btn').forEach(btn => {
        btn.addEventListener('click', () => {
            period = btn.dataset.period;
            renderReports(container);
        });
    });

    container.querySelector('#rep-send').addEventListener('click', () => {
        showToast('Отчёт отправлен на вашу почту');
    });
}
